import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ChevronLeft, Calendar as Cal, Users, MapPin, Clock, Mic2, RefreshCw, ExternalLink,
  CheckCircle2, Circle, AlertCircle, MessageSquareQuote, Sparkles, Loader2, Check, Mail, X,
  ClipboardCheck, ArrowUpRight,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import Markdown from "@/components/Markdown";
import MeetingsCalendar from "@/components/MeetingsCalendar";

const TIME_FMT = { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" };

function fmtDate(iso) {
  try { return new Date(iso).toLocaleString(undefined, TIME_FMT); } catch { return iso; }
}
function fmtDateLong(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: "long", year: "numeric", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

const VIEW = { ALL: "all", OPEN: "open", FLAT: "flat" };

export default function Meetings() {
  const { mid } = useParams();
  const navigate = useNavigate();
  const [meetings, setMeetings] = useState([]);
  const [openActions, setOpenActions] = useState([]);
  const [view, setView] = useState(VIEW.ALL);
  const [topTab, setTopTab] = useState("calendar");
  const [syncing, setSyncing] = useState(false);
  const pollRef = useRef(null);

  const load = async () => {
    const [m, a] = await Promise.all([
      api.get("/meetings"),
      api.get("/actions/open"),
    ]);
    setMeetings(m.data);
    setOpenActions(a.data);
  };
  useEffect(() => {
    Promise.all([api.get("/meetings"), api.get("/actions/open")]).then(([m, a]) => {
      setMeetings(m.data);
      setOpenActions(a.data);
    });
    // Resume the syncing indicator if a Granola sync is already running.
    api.get("/integrations/granola/status").then((r) => {
      if (r.data.sync_state === "running") { setSyncing(true); pollSync(); }
    }).catch(() => {});
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const pollSync = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.get("/integrations/granola/status");
        if (r.data.sync_state !== "running") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setSyncing(false);
          load();
          if (r.data.last_error) toast.error(`Granola sync issue: ${r.data.last_error}`);
          else toast.success(`Granola synced — ${r.data.last_sync_count} meeting${r.data.last_sync_count === 1 ? "" : "s"} updated.`);
        }
      } catch {
        clearInterval(pollRef.current);
        pollRef.current = null;
        setSyncing(false);
      }
    }, 3000);
  };

  const syncGranola = async () => {
    try {
      setSyncing(true);
      await api.post("/integrations/granola/sync");
      setTopTab("recorded");
      pollSync();
    } catch (e) {
      setSyncing(false);
      toast.error(e?.response?.data?.detail || "Couldn't start Granola sync.");
    }
  };

  // Detail mode
  if (mid) {
    return <MeetingDetail mid={mid} onBack={() => navigate("/meetings")} onChanged={load} />;
  }

  const openCount = openActions.length;
  const meetingsWithOpen = meetings.filter((m) => (m.action_items || []).some((a) => !a.done));

  const list = view === VIEW.OPEN ? meetingsWithOpen : meetings;

  return (
    <>
      <div className="surface-header">
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div className="surface-title">Meetings</div>
          {topTab === "recorded" && (
            <button
              onClick={() => setView(VIEW.FLAT)}
              data-testid="open-actions-badge"
              style={{
                fontSize: 11, padding: "5px 11px", borderRadius: 999,
                background: view === VIEW.FLAT ? "rgba(123,111,232,0.20)" : "rgba(123,111,232,0.12)",
                border: "1px solid rgba(123,111,232,0.45)",
                color: "var(--ph-accent-purple)", letterSpacing: "0.08em",
                cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6,
                fontWeight: 600,
              }}
            >
              <span className="dot" style={{ width: 6, height: 6, borderRadius: 50, background: "var(--ph-accent-purple)", display: "inline-block" }} />
              {openCount} OPEN ACTIONS
            </button>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button className="btn btn-primary" data-testid="granola-sync-btn" onClick={syncGranola} disabled={syncing}>
            <RefreshCw size={15} className={syncing ? "spin" : ""} /> {syncing ? "Syncing…" : "Sync Granola"}
          </button>
          <span className="status-pill"><span className="dot" />{topTab === "calendar" ? "Live from Microsoft 365" : "Notes from Granola"}</span>
        </div>
      </div>

      {/* Top tabs: Calendar vs Recorded */}
      <div style={{ padding: "14px 32px 0", display: "flex", gap: 8 }} data-testid="meetings-top-tabs">
        <SubTab label="Calendar" active={topTab === "calendar"} onClick={() => setTopTab("calendar")} testid="top-tab-calendar" />
        <SubTab label="Recorded" active={topTab === "recorded"} onClick={() => setTopTab("recorded")} testid="top-tab-recorded" />
      </div>

      {topTab === "calendar" ? (
        <div style={{ flex: 1, overflowY: "auto", padding: "20px clamp(14px, 4vw, 32px) 60px" }}>
          <div style={{ maxWidth: 980, margin: "0 auto" }}>
            <MeetingsCalendar />
          </div>
        </div>
      ) : (
        <>
          {/* Recorded sub-tabs */}
          <div style={{ padding: "14px 32px 0", display: "flex", gap: 8 }} data-testid="meetings-subtabs">
            <SubTab label="All meetings" active={view === VIEW.ALL} onClick={() => setView(VIEW.ALL)} testid="tab-all" />
            <SubTab label="Has open actions" active={view === VIEW.OPEN} onClick={() => setView(VIEW.OPEN)} testid="tab-open" />
            <SubTab label="Open action items list" active={view === VIEW.FLAT} onClick={() => setView(VIEW.FLAT)} testid="tab-flat" />
          </div>

          <div style={{ flex: 1, overflowY: "auto", padding: "20px clamp(14px, 4vw, 32px) 60px" }}>
            <div style={{ maxWidth: 880, margin: "0 auto" }}>
              {view === VIEW.FLAT ? (
                <OpenActionsFlat actions={openActions} onOpenMeeting={(id) => navigate(`/meetings/${id}`)} onChanged={load} />
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  {list.length === 0 && (
                    <div className="text-dim" style={{ padding: 24 }}>
                      {view === VIEW.OPEN ? "No meetings with open action items." : "No meetings yet."}
                    </div>
                  )}
                  {list.map((m) => (
                    <MeetingCard key={m.id} meeting={m} onOpen={() => navigate(`/meetings/${m.id}`)} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
}

function GranolaBadge() {
  return (
    <span
      data-testid="granola-badge"
      style={{
        marginLeft: 8, fontSize: 9, letterSpacing: "0.12em", padding: "2px 7px",
        borderRadius: 999, background: "rgba(78,205,196,0.12)",
        border: "1px solid rgba(78,205,196,0.45)", color: "var(--ph-product-teal)",
        fontWeight: 700, verticalAlign: "middle", whiteSpace: "nowrap",
      }}
    >
      GRANOLA
    </span>
  );
}

function SubTab({ label, active, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={active ? "btn" : "btn btn-ghost"}
      style={{
        fontSize: 13,
        padding: "9px 16px",
        background: active ? "var(--panel-3)" : "transparent",
        borderColor: active ? "var(--ph-product-teal)" : "transparent",
        color: active ? "var(--ink)" : "var(--ink-dim)",
      }}
    >
      {label}
    </button>
  );
}

function MeetingCard({ meeting, onOpen }) {
  const openItems = (meeting.action_items || []).filter((a) => !a.done);
  const openCount = openItems.length;
  return (
    <div
      data-testid={`meeting-card-${meeting.id}`}
      onClick={onOpen}
      className="fade-up"
      style={{
        position: "relative",
        background: "var(--panel)",
        border: "1px solid var(--hairline)",
        borderLeft: `3px solid ${meeting.color || "#6B5CE7"}`,
        borderRadius: 12,
        padding: "18px 22px",
        cursor: "pointer",
        transition: "border-color 160ms ease, background 160ms ease",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = "var(--ph-product-teal)"; e.currentTarget.style.borderLeftColor = meeting.color || "#6B5CE7"; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--hairline)"; e.currentTarget.style.borderLeftColor = meeting.color || "#6B5CE7"; }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 14 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--ink)", fontWeight: 600 }}>
            {meeting.title}
            {meeting.source === "granola" && <GranolaBadge />}
          </div>
          <div className="text-dim" style={{ fontSize: 11.5, marginTop: 6, letterSpacing: "0.04em" }}>
            {fmtDate(meeting.date)}
            {meeting.duration_min ? ` · ${meeting.duration_min} min` : ""}
            {meeting.attendees?.length ? ` · ${meeting.attendees.join(", ")}` : ""}
            {openCount > 0 && (
              <>
                {" · "}
                <span style={{
                  color: "var(--ph-accent-purple)", fontWeight: 600,
                  padding: "1px 7px", borderRadius: 999,
                  background: "rgba(123,111,232,0.12)", border: "1px solid rgba(123,111,232,0.40)",
                  fontSize: 10, letterSpacing: "0.1em",
                }}>{openCount} OPEN</span>
              </>
            )}
          </div>
        </div>
      </div>

      {openItems.length > 0 && (
        <div
          style={{
            marginTop: 14,
            border: "1px solid rgba(123,111,232,0.30)",
            background: "rgba(123,111,232,0.06)",
            borderRadius: 10,
            padding: "12px 14px",
          }}
          data-testid={`open-actions-${meeting.id}`}
        >
          <div style={{ fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 8, fontWeight: 600, color: "var(--ph-accent-purple)" }}>
            Open actions
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {openItems.slice(0, 3).map((a, i) => (
              <div key={i} style={{ fontSize: 13, color: "var(--ink-soft)", display: "flex", gap: 8, alignItems: "flex-start" }}>
                <Circle size={13} style={{ marginTop: 2, color: "var(--ink-dim)", flexShrink: 0 }} />
                <div>
                  {a.owner && <strong style={{ color: "var(--ink)" }}>{a.owner}</strong>}
                  {a.owner ? " — " : ""}{a.text}
                  {a.due && <span className="text-dim" style={{ marginLeft: 6 }}>· due {a.due}</span>}
                </div>
              </div>
            ))}
            {openItems.length > 3 && (
              <div className="text-dim" style={{ fontSize: 12, marginTop: 4 }}>+ {openItems.length - 3} more…</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function OpenActionsFlat({ actions, onOpenMeeting, onChanged }) {
  const toggle = async (a) => {
    await api.patch(`/meetings/${a.meeting_id}/action/${a.idx}`);
    onChanged && onChanged();
  };
  if (actions.length === 0) {
    return <div className="text-dim" style={{ padding: 24 }}>No open action items. Nice.</div>;
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {actions.map((a, i) => (
        <div
          key={`${a.meeting_id}-${a.idx}`}
          className="fade-up"
          data-testid={`flat-action-${a.meeting_id}-${a.idx}`}
          style={{
            display: "flex", gap: 12, alignItems: "flex-start",
            padding: "14px 16px",
            background: "var(--panel)",
            border: "1px solid var(--hairline)",
            borderLeft: `3px solid ${a.meeting_color || "#6B5CE7"}`,
            borderRadius: 10,
          }}
        >
          <button
            onClick={() => toggle(a)}
            data-testid={`flat-action-toggle-${a.meeting_id}-${a.idx}`}
            style={{ background: "transparent", border: "none", cursor: "pointer", padding: 0, marginTop: 2, flexShrink: 0 }}
            title="Mark done"
          >
            <Circle size={16} style={{ color: "var(--ink-dim)" }} />
          </button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13.5, color: "var(--ink)" }}>
              {a.owner && <strong>{a.owner}</strong>}{a.owner ? " — " : ""}{a.text}
            </div>
            <div className="text-dim" style={{ fontSize: 11.5, marginTop: 4, display: "flex", gap: 8, flexWrap: "wrap" }}>
              <span
                onClick={() => onOpenMeeting(a.meeting_id)}
                style={{ cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3, textDecorationColor: "var(--hairline)" }}
              >
                {a.meeting_title}
              </span>
              <span>· {fmtDate(a.meeting_date)}</span>
              {a.due && <span>· due {a.due}</span>}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// =============================================================
// Detail page
// =============================================================
function MeetingDetail({ mid, onBack, onChanged }) {
  const navigate = useNavigate();
  const [m, setM] = useState(null);
  const [tab, setTab] = useState("summary");
  const [debriefOpen, setDebriefOpen] = useState(false);

  useEffect(() => {
    api.get(`/meetings/${mid}`).then((r) => setM(r.data));
  }, [mid]);

  const toggle = async (idx) => {
    const r = await api.patch(`/meetings/${mid}/action/${idx}`);
    setM((prev) => prev ? { ...prev, action_items: r.data.action_items } : prev);
    onChanged && onChanged();
  };

  if (!m) {
    return (
      <>
        <div className="surface-header">
          <button onClick={onBack} className="btn btn-ghost" data-testid="back-to-meetings">
            <ChevronLeft size={14} /> All meetings
          </button>
        </div>
        <div style={{ padding: 32 }} className="text-dim">Loading…</div>
      </>
    );
  }

  // A meeting is "debriefable" once it's in the past AND has either a transcript
  // or a Granola summary to work from. Drafts-only — never auto-sends.
  const isPast = m.date && new Date(m.date) < new Date();
  const hasContent = !!(m.transcript || m.summary || (m.action_items && m.action_items.length));
  const canDebrief = isPast && hasContent;

  return (
    <>
      <div className="surface-header" style={{ paddingBottom: 14 }}>
        <button onClick={onBack} className="btn btn-ghost" data-testid="back-to-meetings"
          style={{ fontSize: 13 }}>
          <ChevronLeft size={16} strokeWidth={2} /> All meetings
        </button>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {canDebrief && (
            <button
              className="btn btn-primary"
              data-testid="open-debrief-btn"
              onClick={() => setDebriefOpen(true)}
              style={{ fontSize: 13 }}
              title="Generate an AI debrief, commitments, and a thank-you reply draft"
            >
              <ClipboardCheck size={14} /> Debrief
            </button>
          )}
          <span className="status-pill"><span className="dot" />{m.source === "granola" ? "Synced from Granola" : m.recording_url ? "Recording attached" : "No recording"}</span>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "8px clamp(14px, 4vw, 32px) 60px" }}>
        <div style={{ maxWidth: 820, margin: "0 auto" }} className="fade-up">

          {/* Title */}
          <div style={{ marginTop: 18 }}>
            <div style={{ fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--ink)", fontWeight: 600 }}>
              {m.title}
              {m.source === "granola" && <GranolaBadge />}
            </div>
            <div className="text-dim" style={{ fontSize: 11.5, marginTop: 6, letterSpacing: "0.04em" }}>
              {fmtDate(m.date)}
              {m.duration_min ? ` · ${m.duration_min} min` : ""}
              {m.attendees?.length ? ` · ${m.attendees.join(", ")}` : ""}
            </div>
            {m.granola_url && (
              <a
                href={m.granola_url}
                target="_blank"
                rel="noreferrer"
                className="text-cobalt"
                style={{ fontSize: 11.5, marginTop: 6, display: "inline-flex", alignItems: "center", gap: 5, textDecoration: "underline", textUnderlineOffset: 3 }}
                data-testid="granola-open-link"
              >
                <ExternalLink size={11} /> Open in Granola
              </a>
            )}
            {m.recording_url && (
              <a
                href="#"
                onClick={(e) => e.preventDefault()}
                className="text-cobalt"
                style={{ fontSize: 11.5, marginTop: 6, display: "inline-block", textDecoration: "underline", textUnderlineOffset: 3 }}
                data-testid="recording-link"
              >
                <Mic2 size={11} style={{ display: "inline", marginRight: 6 }} />
                {m.recording_url}
              </a>
            )}
          </div>

          <div style={{ height: 1, background: "var(--hairline-soft)", margin: "22px 0 18px" }} />

          {/* Tabs */}
          <div style={{ display: "flex", gap: 22, borderBottom: "1px solid var(--hairline-soft)", marginBottom: 22 }} data-testid="meeting-tabs">
            <DetailTab label="Summary" active={tab === "summary"} onClick={() => setTab("summary")} testid="tab-summary" />
            <DetailTab label="Actions" active={tab === "actions"} onClick={() => setTab("actions")} testid="tab-actions" />
            <DetailTab label="Transcript" active={tab === "transcript"} onClick={() => setTab("transcript")} testid="tab-transcript" />
          </div>

          {tab === "summary" && <SummaryTab m={m} onToggle={toggle} />}
          {tab === "actions" && <ActionsTab m={m} onToggle={toggle} />}
          {tab === "transcript" && <TranscriptTab m={m} />}
        </div>
      </div>

      {debriefOpen && (
        <DebriefModal
          mid={mid}
          meetingTitle={m.title}
          onClose={() => setDebriefOpen(false)}
          onOpenInChat={(starter) => {
            setDebriefOpen(false);
            navigate("/chat", { state: { starter } });
          }}
        />
      )}
    </>
  );
}

function DetailTab({ label, active, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      style={{
        background: "transparent",
        border: "none",
        padding: "12px 4px",
        cursor: "pointer",
        fontSize: 14,
        color: active ? "var(--ink)" : "var(--ink-dim)",
        borderBottom: active ? "2px solid var(--ph-product-teal)" : "2px solid transparent",
        fontWeight: active ? 600 : 500,
        marginBottom: -1,
      }}
    >
      {label}
    </button>
  );
}

function MetaPanel({ Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="panel-2" style={{ padding: "12px 16px", display: "flex", alignItems: "center", gap: 12 }}>
      <Icon size={14} style={{ color: "var(--ink-dim)", flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <span className="text-dim" style={{ fontSize: 11, fontStyle: "italic" }}>{label}: </span>
        <span style={{ fontSize: 13.5, color: "var(--ink-soft)", fontStyle: "italic" }}>{value}</span>
      </div>
    </div>
  );
}

function Section({ title, children, testid }) {
  return (
    <div style={{ marginTop: 26 }} data-testid={testid}>
      <div style={{ fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--ink)", fontWeight: 600, marginBottom: 10 }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function SummaryTab({ m, onToggle }) {
  return (
    <div>
      <h1 style={{ fontSize: 36, marginBottom: 20 }}>{m.title} — Summary</h1>

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <MetaPanel Icon={Cal} label="When" value={fmtDateLong(m.date)} />
        {m.duration_min && <MetaPanel Icon={Clock} label="Duration" value={`${m.duration_min} min`} />}
        {m.attendees?.length > 0 && <MetaPanel Icon={Users} label="Attendees" value={m.attendees.join(", ")} />}
        {m.location && <MetaPanel Icon={MapPin} label="Location" value={m.location} />}
      </div>

      {m.tldr && (
        <Section title="TL;DR" testid="section-tldr">
          <div className="text-soft" style={{ fontSize: 14.5, lineHeight: 1.7 }}>{m.tldr}</div>
        </Section>
      )}

      {m.source === "granola" && m.summary && (
        <Section title="Meeting notes" testid="section-granola-notes">
          <div className="panel-2" style={{ padding: "16px 20px", borderRadius: 10 }}>
            <Markdown text={m.summary} />
          </div>
        </Section>
      )}

      {m.key_topics?.length > 0 && (
        <Section title="Key topics" testid="section-key-topics">
          <ul style={{ paddingLeft: 18, margin: 0, color: "var(--ink-soft)", fontSize: 14, lineHeight: 1.7 }}>
            {m.key_topics.map((t, i) => <li key={i}>{t}</li>)}
          </ul>
        </Section>
      )}

      {m.decisions?.length > 0 && (
        <Section title="Decisions made" testid="section-decisions">
          <ul style={{ paddingLeft: 18, margin: 0, color: "var(--ink-soft)", fontSize: 14, lineHeight: 1.7 }}>
            {m.decisions.map((d, i) => <li key={i}>{d}</li>)}
          </ul>
        </Section>
      )}

      {(m.action_items?.length > 0) && (
        <Section title="Action items" testid="section-actions">
          <ActionList items={m.action_items} onToggle={onToggle} />
        </Section>
      )}

      {m.open_questions?.length > 0 && (
        <Section title="Open questions" testid="section-questions">
          <ul style={{ paddingLeft: 18, margin: 0, color: "var(--ink-soft)", fontSize: 14, lineHeight: 1.7 }}>
            {m.open_questions.map((q, i) => <li key={i}>{q}</li>)}
          </ul>
        </Section>
      )}

      {m.followups?.length > 0 && (
        <Section title="Followups for you" testid="section-followups">
          <ul style={{ paddingLeft: 18, margin: 0, color: "var(--ink-soft)", fontSize: 14, lineHeight: 1.7 }}>
            {m.followups.map((f, i) => <li key={i}>{f}</li>)}
          </ul>
        </Section>
      )}

      {m.notable_quotes?.length > 0 && (
        <Section title="Notable quotes" testid="section-quotes">
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {m.notable_quotes.map((q, i) => (
              <blockquote
                key={i}
                className="font-serif-italic"
                style={{
                  margin: 0, padding: "8px 16px", borderLeft: "2px solid var(--gold)",
                  fontSize: 15, color: "var(--ink-soft)",
                }}
              >
                <MessageSquareQuote size={11} style={{ display: "inline", marginRight: 6, color: "var(--gold)" }} />
                {q}
              </blockquote>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}

function ActionList({ items, onToggle }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {items.map((a, i) => (
        <div
          key={i}
          onClick={() => onToggle(i)}
          data-testid={`action-${i}`}
          style={{
            display: "flex", gap: 10, alignItems: "flex-start",
            padding: "10px 12px", borderRadius: 8,
            background: "var(--panel-2)",
            border: "1px solid var(--hairline)",
            cursor: "pointer",
          }}
        >
          {a.done ? (
            <CheckCircle2 size={16} style={{ color: "var(--good)", marginTop: 1, flexShrink: 0 }} />
          ) : (
            <Circle size={16} style={{ color: "var(--ink-dim)", marginTop: 1, flexShrink: 0 }} />
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: 13.5,
              color: a.done ? "var(--ink-dim)" : "var(--ink)",
              textDecoration: a.done ? "line-through" : "none",
            }}>
              {a.owner && <strong>{a.owner}</strong>}{a.owner ? " — " : ""}{a.text}
            </div>
            {a.due && (
              <div className="text-dim" style={{ fontSize: 11.5, marginTop: 3 }}>
                Due: {a.due}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function ActionsTab({ m, onToggle }) {
  const open = (m.action_items || []).filter((a) => !a.done);
  const done = (m.action_items || []).filter((a) => a.done);
  return (
    <div>
      <div style={{ fontSize: 13, color: "var(--ink-soft)", marginBottom: 14 }}>
        <strong className="text-gold">{open.length}</strong> open ·{" "}
        <strong className="text-good">{done.length}</strong> complete
      </div>
      {open.length > 0 && (
        <>
          <div className="text-dim" style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 8 }}>Open</div>
          <ActionList items={open.map((a, i) => ({ ...a, _orig_idx: (m.action_items || []).indexOf(a) }))}
                      onToggle={(localIdx) => onToggle(open[localIdx]._orig_idx ?? (m.action_items || []).indexOf(open[localIdx]))} />
        </>
      )}
      {done.length > 0 && (
        <>
          <div className="text-dim" style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", marginTop: 22, marginBottom: 8 }}>Complete</div>
          <ActionList items={done.map((a) => ({ ...a }))}
                      onToggle={(localIdx) => onToggle((m.action_items || []).indexOf(done[localIdx]))} />
        </>
      )}
      {(m.action_items || []).length === 0 && <div className="text-dim">No action items.</div>}
    </div>
  );
}

function TranscriptTab({ m }) {
  if (!m.transcript) {
    return <div className="text-dim" style={{ fontSize: 13 }}>No transcript available for this meeting.</div>;
  }
  return (
    <div>
      <div className="text-dim" style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
        <AlertCircle size={12} /> {m.source === "granola" ? "Transcript from Granola" : "Transcribed locally · never uploaded"}
      </div>
      <pre
        data-testid="transcript-body"
        style={{
          fontFamily: "inherit", whiteSpace: "pre-wrap",
          fontSize: 14, lineHeight: 1.7, color: "var(--ink-soft)",
          padding: 18, borderRadius: 10,
          background: "var(--panel-2)", border: "1px solid var(--hairline)",
          margin: 0,
        }}
      >
        {m.transcript}
      </pre>
    </div>
  );
}


// =============================================================
// Debrief Modal — POST /api/meetings/{mid}/debrief
// Calm post-meeting recap with commitments + a paste-ready thank-you draft.
// Drafts-only: never sends. User can save the debrief to Vault or open the
// thank-you in Chat where Andre routes it through the existing email draft.
// =============================================================
function DebriefModal({ mid, meetingTitle, onClose, onOpenInChat }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedToVault, setSavedToVault] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.post(`/meetings/${mid}/debrief`);
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Couldn't build the debrief.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [mid]);

  const saveToVault = async () => {
    if (!data || saving || savedToVault) return;
    setSaving(true);
    try {
      await api.post("/vault/docs", {
        user_id: "_",
        folder: "00 Inbox",
        name: data.title || `Debrief — ${meetingTitle || "meeting"}`,
        markdown: data.markdown || "",
        tags: ["debrief", "meeting"],
      });
      setSavedToVault(true);
      toast.success("Debrief saved to Vault → 00 Inbox.");
    } catch {
      toast.error("Couldn't save the debrief to your vault.");
    } finally {
      setSaving(false);
    }
  };

  const openThanksInChat = () => {
    if (!data?.thanks_draft) return;
    const td = data.thanks_draft;
    // Hand the thank-you off to Andre as a real email-draft request. He will
    // produce a DraftCard the user can route into Outlook Drafts (drafts-only).
    const starter =
      `Draft a thank-you email reply for the meeting "${meetingTitle || ""}".\n\n` +
      `To: ${td.to || ""}\n` +
      `Subject: ${td.subject || ""}\n\n` +
      `Use this body verbatim as the starting point — keep it calm and short:\n\n${td.body || ""}`;
    onOpenInChat(starter);
  };

  return (
    <div
      data-testid="debrief-modal"
      onMouseDown={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 80,
        display: "flex", alignItems: "center", justifyContent: "center",
        background: "rgba(12,12,16,0.55)", backdropFilter: "blur(4px)",
        padding: 24,
      }}
    >
      <div
        onMouseDown={(e) => e.stopPropagation()}
        className="fade-up"
        style={{
          width: "100%", maxWidth: 640,
          background: "var(--panel)",
          border: "1px solid var(--hairline)",
          borderRadius: 16,
          boxShadow: "0 24px 60px rgba(0,0,0,0.35)",
          overflow: "hidden",
          maxHeight: "calc(100vh - 48px)",
          display: "flex", flexDirection: "column",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 22px 0" }}>
          <div style={{ fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--ph-accent-purple)", fontWeight: 600 }}>
            Meeting debrief
          </div>
          <button
            onClick={onClose}
            data-testid="debrief-close"
            style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--ink-dim)" }}
            aria-label="Close debrief"
          >
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: "10px 22px 22px", overflowY: "auto" }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: "var(--ink)", lineHeight: 1.3 }} data-testid="debrief-title">
            {data?.title || `Debrief — ${meetingTitle || "Meeting"}`}
          </div>
          {data?.had_prep_pack && (
            <div className="text-dim" style={{ fontSize: 11.5, marginTop: 6, letterSpacing: "0.04em" }} data-testid="debrief-prep-pack-stitched">
              <Sparkles size={11} style={{ display: "inline", verticalAlign: -1, marginRight: 4, color: "var(--ph-product-teal)" }} />
              Prep pack from before the meeting was stitched in.
            </div>
          )}

          {loading && (
            <div
              data-testid="debrief-loading"
              style={{ display: "flex", alignItems: "center", gap: 10, color: "var(--ink-soft)", fontSize: 13.5, padding: "26px 0" }}
            >
              <Loader2 size={14} className="spin" />
              Building your debrief — pulling commitments, decisions, and drafting the thank-you…
            </div>
          )}

          {error && !loading && (
            <div
              data-testid="debrief-error"
              style={{
                marginTop: 18, padding: "14px 16px",
                borderRadius: 12, background: "rgba(255,107,107,0.08)",
                border: "1px solid rgba(255,107,107,0.32)",
                color: "var(--ink-soft)", fontSize: 13.5,
              }}
            >
              {error}
            </div>
          )}

          {data && !loading && !error && (
            <>
              {data.summary && (
                <div
                  data-testid="debrief-summary"
                  style={{
                    fontSize: 13.5, lineHeight: 1.6,
                    color: "var(--ink-soft)",
                    marginTop: 14, padding: "10px 12px",
                    borderLeft: "3px solid var(--ph-product-teal)",
                    background: "var(--panel-2)", borderRadius: 4,
                  }}
                >
                  {data.summary}
                </div>
              )}

              <div
                className="prep-md"
                data-testid="debrief-markdown"
                style={{
                  fontSize: 13.5, lineHeight: 1.7, color: "var(--ink)",
                  marginTop: 16,
                }}
              >
                <Markdown text={data.markdown || ""} />
              </div>

              {data.thanks_draft?.body && (
                <div
                  data-testid="debrief-thanks-block"
                  style={{
                    marginTop: 20, padding: "14px 16px",
                    borderRadius: 12,
                    background: "rgba(78,205,196,0.07)",
                    border: "1px solid rgba(78,205,196,0.30)",
                  }}
                >
                  <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--ph-product-teal)", fontWeight: 600, marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
                    <Mail size={11} /> Thank-you draft
                  </div>
                  {data.thanks_draft.to && (
                    <div className="text-dim" style={{ fontSize: 12, marginBottom: 4 }}>
                      <strong style={{ color: "var(--ink-soft)" }}>To:</strong> {data.thanks_draft.to}
                    </div>
                  )}
                  {data.thanks_draft.subject && (
                    <div className="text-dim" style={{ fontSize: 12, marginBottom: 8 }}>
                      <strong style={{ color: "var(--ink-soft)" }}>Subject:</strong> {data.thanks_draft.subject}
                    </div>
                  )}
                  <div
                    data-testid="debrief-thanks-body"
                    style={{
                      fontSize: 13.5, lineHeight: 1.65, color: "var(--ink)",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {data.thanks_draft.body}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {data && !loading && !error && (
          <div
            style={{
              display: "flex", gap: 10, justifyContent: "flex-end",
              padding: "14px 22px",
              borderTop: "1px solid var(--hairline-soft)",
              background: "var(--panel-2)",
              flexWrap: "wrap",
            }}
          >
            <button className="btn btn-ghost" onClick={onClose} data-testid="debrief-cancel">Close</button>
            <button
              className="btn"
              onClick={saveToVault}
              disabled={saving || savedToVault}
              data-testid="debrief-save-vault"
            >
              {saving ? <Loader2 size={14} className="spin" /> : <Check size={14} />} {savedToVault ? "Saved" : "Save to Vault"}
            </button>
            {data.thanks_draft?.body && (
              <button
                className="btn btn-primary"
                onClick={openThanksInChat}
                data-testid="debrief-open-in-chat"
              >
                <ArrowUpRight size={14} /> Open thank-you in Chat
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
